// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

// Relative timestamp for a chat message. Kept out of the component so it can be
// unit-tested with a fixed clock (the injectable `now`) without rendering React.

import { copy } from "@/lib/copy"

// The app is Spanish-only: pass 'es-MX' explicitly so the month abbrev is
// Spanish regardless of the browser locale.
const LOCALE = "es-MX"

// es-MX defaults to a 12-hour clock ("02:32 p.m."); hour12:false pins the
// 24-hour "14:32" the chat uses.
const TIME_OPTS: Intl.DateTimeFormatOptions = {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
}

/**
 * Whole-day distance between two instants, measured midnight-to-midnight in
 * local time (not a rolling 24h window) — so a 23:00 message read at 01:00 the
 * next day counts as "yesterday", matching how a person reads the calendar.
 *
 * @param msg - the message instant.
 * @param now - the reference "now".
 * @returns days from `msg`'s local midnight to `now`'s local midnight.
 */
const dayDiff = (msg: Date, now: Date): number => {
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  return Math.round((startOf(now) - startOf(msg)) / 86_400_000)
}

/**
 * Relative timestamp for a chat message: bare time for today, "Ayer, HH:MM" for
 * yesterday, and "<d mon>, HH:MM" (with the year only when it differs from the
 * current year) for anything older. `now` is injectable for deterministic tests.
 *
 * Returns "" for a missing or unparseable timestamp — reopened transcripts can
 * carry odd/absent timestamps and must never render "Invalid Date".
 *
 * @param timestamp - the message's ISO-8601 timestamp (possibly empty/odd).
 * @param now - reference clock (defaults to the current time).
 * @returns the localized relative timestamp, or "" when unrenderable.
 */
export const formatMessageTime = (timestamp: string, now: Date = new Date()): string => {
  if (!timestamp) return ""
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return ""

  const time = date.toLocaleTimeString(LOCALE, TIME_OPTS)
  const diff = dayDiff(date, now)

  if (diff === 0) return time
  if (diff === 1) return `${copy.chat.yesterdayPrefix}, ${time}`

  const dateOpts: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" }
  if (date.getFullYear() !== now.getFullYear()) dateOpts.year = "numeric"
  return `${date.toLocaleDateString(LOCALE, dateOpts)}, ${time}`
}
